import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { ROUTE_PATH } from "@/constants/routeConstants";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useAzureAuth } from "@/hooks/useAzureAuth";
import { useSilentSso } from "@/hooks/useSilentSso";
import { useVerifiedSession } from "@/hooks/useVerifiedSession";
import { stripSsoHints } from "@/utils/silentSso";
import loginbackground from "@/assets/svg/loginbackground.svg";
import LandingPageLightIcon from "@/assets/svg/alleralandingpageLogo.svg";
import landingPageDarkIcon from "@/assets/svg/alleraLandingPageDarkLogo.svg";
import loginbackgroundDark from "@/assets/svg/loginbackgrounddarkmode.svg";

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginRedirect, isAuthenticated, isLoading, error, inProgress } = useAzureAuth();
  const { user } = useSelector((state: any) => state.auth);
  const fromLocation = (location.state as { from?: { pathname?: string; search?: string } } | undefined)?.from;

  // Sign in silently if the browser already has an Entra ID session
  const isVerified = useVerifiedSession();
  // (Re-)establish the session from the current Microsoft identity unless this tab already has it
  const { isChecking } = useSilentSso(!isVerified, user);

  /**
   * Handle login button click
   * Initiates Azure Entra ID authentication flow
   */
  const handleLogin = async () => {
    try {
      // Return to the page the user originally opened (e.g. /dashboard/<patient>) after login
      const redirectStartPage = fromLocation?.pathname
        ? `${window.location.origin}${fromLocation.pathname}${stripSsoHints(fromLocation.search ?? "")}`
        : undefined;
      await loginRedirect(redirectStartPage);
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  /**
   * Redirect to dashboard if already authenticated
   * Use replace: true to prevent back button issues
   * Only redirect if both MSAL and Redux show authenticated AND not loading
   */
  useEffect(() => {
    if (isAuthenticated && isVerified && !isLoading && inProgress === "none") {
      const state = location.state as { from?: { pathname?: string } } | undefined;
      const targetPath = state?.from?.pathname || ROUTE_PATH.ROUTE_DASHBOARD;
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, isVerified, isLoading, navigate, inProgress, location.state]);

  // Full page loader while checking for an existing Entra ID session
  if (isChecking) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4'></div>
          <p className='text-lg font-medium text-foreground'>
            Loading Session...
          </p>
          <p className='text-sm text-muted-foreground mt-2'>
            Please wait while we sign you in
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='
        w-full h-screen flex items-center justify-center p-4 
        bg-[#F5F9FA] dark:bg-[#061A23]
        relative overflow-hidden
      '
    >
      <div className='fixed inset-0 z-0 pointer-events-none'>
        <img
          src={loginbackground}
          alt='Login background'
          className='block dark:hidden w-full h-full object-cover xl:object-fill'
        />
        <img
          src={loginbackgroundDark}
          alt='Login background dark'
          className='hidden dark:block w-full h-full object-cover xl:object-fill'
        />
      </div>
      <div
        className='
      bg-white border border-[#E5E5E5] dark:border-[#737373] 
      dark:bg-[#0A2B39] relative z-10
    '
        style={{
          width: "454px",
          maxWidth: "100%",
          padding: "40px",
          borderRadius: "16px",
          boxShadow: "0px 0px 16px 0px #005C893D",
        }}
      >
        <div className='flex flex-col gap-10'>
          <div>
            <div className='flex items-center justify-center h-11'>
              <img
                src={LandingPageLightIcon}
                alt='Dashboard'
                className='block dark:hidden w-[240px] h-[59px] '
              />
              <img
                src={landingPageDarkIcon}
                alt='Dashboard'
                className='hidden dark:block w-[240px] h-[59px] dark:opacity-70'
              />
            </div>
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography
              variant='semibold-22'
              className='text-center text-grey-800 transition-colors dark:text-grey-200'>
              Welcome to ONEview
            </Typography>
            <Typography
              className='text-grey-600 text-center font-inter sm:whitespace-nowrap dark:text-grey-400'
              style={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
              }}>
              Login using your organization's Microsoft account
            </Typography>
          </div>

          {/* Error Message */}
          {error && (
            <div className='p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md'>
              <Typography
                variant='regular-16'
                className='text-red-600 dark:text-red-400 text-center text-sm'>
                {error}
              </Typography>
            </div>
          )}

          {/* Login button */}
          <Button
            type='button'
            onClick={handleLogin}
            disabled={isLoading}
            className='w-full bg-blue-600 hover:bg-blue-700 text-white dark:bg-primary-600 dark:hover:bg-primary-700 font-medium py-6 rounded-md shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed'>
            <div className='flex items-center justify-center gap-3'>
              {isLoading ? (
                <>
                  <svg
                    className='animate-spin h-5 w-5 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <Typography
                    variant='medium-16'
                    className='text-grey-100 dark:text-grey-100'>
                    Signing in...
                  </Typography>
                </>
              ) : (
                <>
                  <svg
                    className='w-5 h-5'
                    viewBox='0 0 21 21'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'>
                    <rect x='1' y='1' width='9' height='9' fill='#F25022' />
                    <rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
                    <rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
                    <rect x='11' y='11' width='9' height='9' fill='#FFB900' />
                  </svg>
                  <Typography
                    variant='medium-16'
                    className='text-grey-100 dark:text-grey-100'>
                    Login with Microsoft
                  </Typography>
                </>
              )}
            </div>
          </Button>
          <div className='text-center'>
            <Typography
              variant='regular-16'
              className='text-grey-600  transition-colors dark:text-grey-400'
              style={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
              }}>
              © 2026 Alera Health. All rights reserved.
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
export default LoginForm;
